import { A } from '@ember/array';
import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import type StoreService from '@ember-data/store';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { ComponentLike } from '@glint/template';
import { call, mapBy, rejectBy, sortBy } from '@nullvoxpopuli/ember-composable-helpers';
import { format, subMinutes, fromUnixTime } from 'date-fns';
import can from 'ember-can/helpers/can';
import type AbilitiesService from 'ember-can/services/abilities';
import { hash, task, timeout } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import link from 'ember-link/helpers/link';
import set from 'ember-set-helper/helpers/set';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import { and, eq, gt, notEq, or } from 'ember-truth-helpers';
import ArrivedAtColumnComponent from 'garaje/components/arrived-at-column';
import BlocklistColumnComponent from 'garaje/components/blocklist-column';
import BlocklistFilterModal from 'garaje/components/blocklist-filter-modal';
import ConnectNotificationActivity from 'garaje/components/connect-notification-activity';
import CustomColumn from 'garaje/components/custom-column';
import InviteSignInModal from 'garaje/components/invite-sign-in-modal';
import LineClamp from 'garaje/components/line-clamp';
import MonogramOrPhoto from 'garaje/components/monogram-or-photo';
import PluginActivityColumnNda from 'garaje/components/plugin-activity-column-nda';
import QuestionnairesStatusColumn from 'garaje/components/questionnaires-status-column';
import ReprintBadgeModal from 'garaje/components/reprint-badge-modal';
import TruncatedColumn from 'garaje/components/truncated-column';
import DropdownMenu from 'garaje/components/ui/classic/dropdown-menu';
import UiButton from 'garaje/components/ui-button';
import UiPill from 'garaje/components/ui-pill';
import config from 'garaje/config/environment';
import awaitHelper from 'garaje/helpers/await';
import dateFormat from 'garaje/helpers/date-format';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type Entry from 'garaje/models/entry';
import type FlowQuestionnaire from 'garaje/models/flow-questionnaire';
import type Invite from 'garaje/models/invite';
import type { UserDataValidationResult } from 'garaje/models/invite';
import type LocationModel from 'garaje/models/location';
import type PrinterModel from 'garaje/models/printer';
import type ReservationModel from 'garaje/models/reservation';
import type SubscriptionModel from 'garaje/models/subscription';
import type User from 'garaje/models/user';
import type { InvitesDashboardField } from 'garaje/pods/visitors/invites/index/controller';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LoggerService from 'garaje/services/logger';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type UploaderFactoryService from 'garaje/services/uploader-factory';
import { createBlocklistFilter, saveBlocklistFilter } from 'garaje/utils/blocklist-utils';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import zft from 'garaje/utils/zero-for-tests';
import { bool, not, and as andMacro, or as orMacro } from 'macro-decorators';
import moment from 'moment-timezone';

type Context = 'location' | 'property';

const USER_DATA_VALIDATION_ICE_CREAM =
  "Some of this invite's responses have been removed or changed. Please review and update.";

interface InvitesTableInviteFeedItemComponentSignature {
  Args: {
    canPrintBadges: boolean;
    checkbox: ComponentLike<{
      Args: {
        item: Invite;
      };
      Element: HTMLDivElement;
    }>;
    /**
     * The context in which the component is being used
     */
    context: Context;
    deleteInvites: (invite: Invite) => void;
    disabled?: boolean;
    fields: InvitesDashboardField[];
    invite: Invite;
    isSelected: boolean;
    location: LocationModel;
    preprintBadge: (invite: Invite, printer?: PrinterModel | null) => void;
    printers: PrinterModel[];
    renderStickyShadows?: boolean;
    showActions?: boolean;
    showFullDate: boolean;
    transitionedFrom?: string;
    vrSubscription: SubscriptionModel;
  };
  Element: HTMLDivElement;
}

export default class InvitesTableInviteFeedItemComponent extends Component<InvitesTableInviteFeedItemComponentSignature> {
  @service declare abilities: AbilitiesService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare logger: LoggerService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare flashMessages: FlashMessagesService;
  @service declare uploaderFactory: UploaderFactoryService;

  @tracked displayPrinterSelectorModal = false;
  @tracked markVisitorAsLate = false;
  @tracked showSignInModal = false;

  @tracked itemFullNameWasTruncated = false;
  @tracked itemHostWasTruncated = false;

  @tracked entryForInvite: Entry | null = null;
  @tracked creatorForInvite: User | null = null;

  @tracked blocklistFilterInModal: BlacklistFilter | null = null;

  id = guidFor(this);
  tooltipDelay = zft(400);

  @bool('args.invite.entry.signInTime') visitorIsSignedIn!: boolean;
  @not('validateInviteTask.lastSuccessful.value.isValid') isUserDataInvalid!: boolean;
  @not('visitorIsSignedIn') visitorIsNotSignedIn!: boolean;
  @bool('args.invite.entry.signOutTime') visitorIsSignedOut!: boolean;
  @not('visitorIsSignedOut') visitorIsNotSignedOut!: boolean;
  @orMacro('args.invite.needsApprovalReview', 'args.invite.approvalWasDenied') visitorIsDisallowedAccess!: boolean;
  @not('visitorIsDisallowedAccess') visitorIsAllowedAccess!: boolean;
  @andMacro('visitorIsSignedIn', 'visitorIsNotSignedOut') markVisitorAsSignedIn!: boolean;
  @andMacro('args.invite.needsApprovalReview', 'isBeforeToday', 'visitorIsNotSignedIn') isExpired!: boolean;

  @orMacro('visitorIsSignedOut', 'markVisitorAsLate', 'markVisitorAsSignedIn', 'isUserDataInvalid')
  showTooltip!: boolean;

  constructor(owner: unknown, args: InvitesTableInviteFeedItemComponentSignature['Args']) {
    super(owner, args);
    void this.validateInviteTask.perform().catch((error) => {
      // Log error but don't block component initialization
      // eslint-disable-next-line no-console
      console.error('Failed to validate invite user data:', error);
    });
    void this.loadInviteRelationshipsTask.perform();
  }

  get reviewerName(): string | undefined {
    return this.args.invite.visitApproval?.screenerReviews.slice()[0]?.reviewerName;
  }

  get query(): Record<string, string> {
    const transitionedFrom = this.args.transitionedFrom ?? 'location';
    return { transitionedFrom };
  }

  get showActions(): boolean {
    const showActions = this.args.showActions ?? true;
    return (!this.args.invite.isFromEmployeeScreening || this.visitorIsNotSignedIn) && showActions;
  }

  get locationNames(): string {
    return this.args.invite.locationNames.join(', ');
  }

  get isBeforeToday(): boolean {
    return moment(this.args.invite.expectedArrivalTime).isBefore(moment(), 'day');
  }

  get deniedColspan(): number {
    // NOTE colspan for the denied message is equal the the number of shown fields minus 1
    // for the name field, which is already in the row and is not included in the colspan
    return this.args.fields.filter((field) => field.show).length - 1;
  }

  get context(): Context {
    return this.args.context ?? 'location';
  }

  get cannotReviewInvite(): boolean {
    return this.abilities.cannot('review entry-approval', {
      context: this.context,
      visitApproval: this.args.invite.visitApproval,
      parent: this.args.invite,
    });
  }

  get fieldCount(): number {
    return A(this.args.fields ?? []).filterBy('show').length;
  }

  get canPrint(): boolean {
    return (
      !this.args.invite.isFromEmployeeScreening &&
      this.visitorIsAllowedAccess &&
      this.args.canPrintBadges &&
      this.args.printers.length > 0
    );
  }

  get canInviteAgain(): boolean {
    return !this.args.invite.isFromEmployeeScreening && !this.args.invite.isFromWalkUp && this.visitorIsAllowedAccess;
  }

  get canInviteGroupAgain(): boolean {
    return Boolean(
      !this.args.invite.isFromEmployeeScreening &&
        !this.args.invite.isFromWalkUp &&
        this.visitorIsAllowedAccess &&
        this.args.invite.groupInviteId,
    );
  }

  get canSignInFromInvite(): boolean {
    return this.abilities.can('create entries') && this.args.vrSubscription?.canSignInFromInvite;
  }

  get canSignIn(): boolean {
    return this.visitorIsNotSignedIn && this.visitorIsAllowedAccess && this.canSignInFromInvite;
  }

  get canDelete(): boolean {
    return this.visitorIsNotSignedIn && this.abilities.can('delete invite', this.args.invite);
  }

  get hasNoOptions(): boolean {
    return !this.canPrint && !this.canInviteAgain && !this.canSignIn && !this.canDelete;
  }

  get userDataValidationError(): string | undefined {
    return this.validateInviteTask.lastSuccessful?.value?.errors?.[0]?.message;
  }

  get entryId(): string | undefined {
    return this.args.invite.belongsTo('entry').id();
  }

  get flowQuestionnaires(): FlowQuestionnaire[] {
    return this.args.invite.flow?.content?.flowQuestionnaires.slice() ?? [];
  }

  loadInviteRelationshipsTask = task(
    waitFor(async () => {
      const { entryId } = this;
      const creatorId = this.args.invite.belongsTo('creator').id();

      const relationshipPromises = {
        entry: entryId ? this.store.peekRecord('entry', entryId) ?? this.args.invite.entry : null,
        creator: creatorId ? this.store.peekRecord('user', creatorId) ?? this.args.invite.creator : null,
      };

      const relationships = await hash(relationshipPromises);

      this.entryForInvite = relationships.entry;
      this.creatorForInvite = relationships.creator;
    }),
  );

  validateInviteTask = task({ drop: true }, async (): Promise<UserDataValidationResult> => {
    const result = await this.args.invite.validateUserData();
    return result;
  });

  markVisitorAsLateTask = task({ on: 'init' }, async () => {
    const expectedAt = moment(this.args.invite.expectedArrivalTime);

    while (true) {
      if (config.environment === 'test' || this.visitorIsSignedIn || this.markVisitorAsLate) {
        return;
      }

      const now = moment();
      const diff = expectedAt.diff(now, 'minutes');
      if (diff < -20) {
        this.markVisitorAsLate = true;
      }
      await timeout(5000);
    }
  });

  @action
  displayReservationTime(reservation: ReservationModel): string {
    if (reservation.isPartialDay) {
      const startTime = fromUnixTime(reservation.startTime);
      const endTime = fromUnixTime(reservation.endTime);
      const start = subMinutes(startTime, this.state.minutesBetweenTimezones(startTime));
      const end = subMinutes(endTime, this.state.minutesBetweenTimezones(endTime));
      return `${format(start, 'h:mmaa')} - ${format(end, 'h:mmaa')}`;
    }
    return 'All day';
  }

  @action
  signIn(): void {
    this.showSignInModal = true;
  }

  @action
  closeSignIn(): void {
    this.showSignInModal = false;
  }

  @action
  async onSuccessSignIn(): Promise<void> {
    await this.args.invite.reload();
    this.entryForInvite = await this.args.invite.entry;
  }

  @action
  trackOpenActionMenu(inviteId: string): void {
    this.metrics.trackEvent('Invite Overflow Opened', { invite_id: inviteId });
  }

  @action
  trackPreprintBadgeRequested(invite: Invite): void {
    const properties = { invite_id: parseInt(invite.id, 10) };
    this.metrics.trackEvent('Preprint Invite Badge Requested', properties);
  }

  @action
  inviteAgain(invite: Invite): void {
    const { visitorEmail: email, fullName: name, id: previousInvite } = invite;
    const qpEmail = email || invite.email; // Fallback to email param

    void this.router.transitionTo('visitors.invites.new', {
      queryParams: { email: qpEmail, name, previousInvite },
    });

    this.metrics.trackEvent('Invites Invited Again', { invite_id: previousInvite });
  }

  @action
  inviteGroupAgain(invite: Invite): void {
    void this.router.transitionTo('visitors.invites.new', {
      queryParams: { type: 'group', reinviteGroupId: invite.groupInviteId },
    });

    this.metrics.trackEvent('Invites Invited Again', { group_invite_id: invite.groupInviteId });
  }

  @action
  preprintBadge(invite: Invite, printer?: PrinterModel | null): void {
    this.args.preprintBadge(invite, printer);
    this.trackPreprintBadgeRequested(invite);
  }

  @action
  createBlocklistFilter(): void {
    this.blocklistFilterInModal = createBlocklistFilter({
      model: this.args.invite,
      store: this.store,
      location: this.state.currentLocation,
    });

    this.metrics.startJob();

    this.metrics.trackJobEvent('Invite - Add to Block List Button Clicked', {
      entry_id: this.args.invite.id,
    });
  }

  saveBlocklistFilterTask = task(
    waitFor(async (blocklistFilter: BlacklistFilter, photo: File | null) => {
      try {
        await saveBlocklistFilter({ blocklistFilter, photo, uploaderFactory: this.uploaderFactory });
        this.blocklistFilterInModal = null;
        this.flashMessages.showAndHideFlash('success', 'Blocklist filter saved');
      } catch (e) {
        this.flashMessages.showFlash('error', parseErrorForDisplay(e));
      }
    }),
  );

  <template>
    <div
      class="invite-feed-item {{if @isSelected 'bg-carbon-5'}} {{if this.isUserDataInvalid 'bg-papaya-5'}} table-row"
      id={{this.id}}
      data-test-invite-feed-item={{@invite.id}}
      data-test-selected={{@isSelected}}
      ...attributes
    >
      <div
        class="table-cell border-b align-middle sticky left-0 text-carbon-50 bg-white
          {{if this.markVisitorAsSignedIn 'preRegSignedIn'}}
          {{if this.visitorIsSignedOut 'preRegSignedOut'}}
          {{if this.markVisitorAsLate 'preRegNoShow'}}
          {{if this.isUserDataInvalid 'preRegInvalid !bg-papaya-5'}}
          {{if @isSelected '!bg-carbon-5'}}
          "
        style="width:6px;min-width:6px;"
      >
        {{#if this.showTooltip}}
          <EmberPopover @popperContainer=".ui__page" @side="top" @tooltipClass="z-50 max-w-[28rem]">
            {{#if this.visitorIsSignedOut}}
              Visitor has signed out
            {{/if}}
            {{#if this.markVisitorAsLate}}
              Visitor is late
            {{/if}}
            {{#if this.markVisitorAsSignedIn}}
              Visitor here
            {{/if}}
            {{#if this.isUserDataInvalid}}
              {{if this.userDataValidationError this.userDataValidationError USER_DATA_VALIDATION_ICE_CREAM}}
            {{/if}}
          </EmberPopover>
        {{/if}}
        {{#if this.isUserDataInvalid}}
          <span class="sr-only" data-test-user-data-invalid>
            {{if this.userDataValidationError this.userDataValidationError USER_DATA_VALIDATION_ICE_CREAM}}
          </span>
        {{/if}}
        &nbsp;
      </div>

      {{#if @checkbox}}
        {{#let (component (ensureSafeComponent @checkbox this)) as |CheckBox|}}
          <div
            class="table-cell align-top border-b pl-1 pt-[26px] mx-1 sticky left-[6px] bg-white z-10
              {{if @isSelected '!bg-carbon-5'}}
              {{if this.isUserDataInvalid '!bg-papaya-5'}}
              "
          >
            <CheckBox @item={{@invite}} class="mx-1" />
          </div>
        {{/let}}
      {{/if}}

      <div
        class="table-cell sticky w-[50px] bg-white z-10
          {{if @checkbox 'left-[31px]' 'left-[6px]'}}
          {{if @isSelected '!bg-carbon-5'}}
          {{if this.isUserDataInvalid '!bg-papaya-5'}}
          "
      >
        <MonogramOrPhoto
          @href={{unless
            @disabled
            (link
              route=(if this.visitorIsSignedIn "visitors.entry" "visitors.invites.show")
              model=(if this.visitorIsSignedIn this.entryId @invite.id)
            )
          }}
          @name={{@invite.fullName}}
          @devicePhoto={{or @invite.photoUrl this.entryForInvite.thumbnails.small}}
          @passportPhoto={{this.entryForInvite.passportAvatar}}
          @align="align-top"
        />
      </div>
      {{! template-lint-disable attribute-indentation }}
      <div
        class="table-cell border-b pl-2 pt-4 text-carbon-50 align-top min-w-[16rem] sticky z-10 before:transition-shadow bg-white before:content-['']
          {{if @checkbox 'left-[97px]' 'left-[72px]'}}
          {{if @isSelected '!bg-carbon-5'}}
          {{if this.isUserDataInvalid '!bg-papaya-5'}}
          {{if
            @renderStickyShadows
            'before:shadow-[4px_0_4px_0px_rgba(0,0,0,0.1)] before:h-full before:w-[4px] before:absolute before:right-0 before:top-0'
          }}
          "
        data-test-invite-id={{@invite.id}}
      >
        <LinkTo
          @disabled={{@disabled}}
          @route={{if this.visitorIsSignedIn "visitors.entry" "visitors.invites.show"}}
          @model={{if this.visitorIsSignedIn this.entryId @invite.id}}
          @query={{this.query}}
          class="flex items-start text-base font-bold no-underline text-carbon-70 trans-color
            {{if @disabled '!no-underline cursor-text' 'hover:text-red-50'}}"
          data-test-invite-link-to={{@invite.id}}
        >
          <LineClamp
            @text={{@invite.fullName}}
            @lines={{if @invite.host 1 2}}
            @lineHeight={{1.25}}
            @handleTruncate={{fn (mut this.itemFullNameWasTruncated)}}
            aria-hidden="true"
            data-private
            data-test-invite-full-name-clamp
          />
          {{#if @invite.hasAdditionalGuests}}
            <div aria-hidden="true">
              <UiPill class="whitespace-nowrap ml-1 mt-[-0.5em]" @fill="grayDark" data-test-additional-guests>
                +{{@invite.additionalGuests}}
                {{pluralize @invite.additionalGuests "visitor" withoutCount=true}}
              </UiPill>
            </div>
          {{/if}}
          {{#if this.itemFullNameWasTruncated}}
            <EmberPopover
              @tooltipClass="break-word w-96 text-sm font-bold"
              @popperContainer=".ui__page"
              @delay={{this.tooltipDelay}}
              @text={{@invite.fullName}}
              data-test-invite-full-name-tooltip
            />
          {{/if}}

          <div class="sr-only">
            {{! Full visitor name available to screen reader }}
            Visitor:
            <span data-test-invite-full-name>{{@invite.fullName}}</span>
            {{#if @invite.hasAdditionalGuests}}
              and
              {{@invite.additionalGuests}}
              {{pluralize @invite.additionalGuests "visitor" withoutCount=true}}
            {{/if}}.

            {{#if @invite.host}}
              {{! Full host name available to screen reader }}
              Host:
              <span data-test-invite-host>{{@invite.host}}</span>.
            {{/if}}
          </div>
        </LinkTo>

        {{#if @invite.host}}
          <p data-test-invite-host-wrapper class={{if @invite.hasAdditionalGuests "m-0" "m-0 mt-1"}}>
            <LineClamp
              @text={{@invite.host}}
              @lines={{1}}
              @lineHeight={{1.25}}
              @handleTruncate={{fn (mut this.itemHostWasTruncated)}}
              class="content-prefix-host"
              aria-hidden="true"
              data-test-invite-host-clamp
            />
            {{#if this.itemHostWasTruncated}}
              <EmberPopover
                @tooltipClass="break-word w-96 text-sm font-normal"
                @popperContainer=".ui__page"
                @delay={{this.tooltipDelay}}
                data-test-invite-host-tooltip
              >
                <strong>Host:</strong>
                <span>{{@invite.host}}</span>
              </EmberPopover>
            {{/if}}
          </p>
        {{/if}}
      </div>

      {{#if (and @invite.approvalWasDenied this.cannotReviewInvite)}}
        <td class="px-2 border-b align-middle" colspan={{this.deniedColspan}} data-test-security-denied>
          <div style="max-width: 500px;">
            This invitation was blocked upon review by
            {{or this.reviewerName "an administrator"}}. Contact
            {{or this.reviewerName "an administrator"}}
            to learn more.
          </div>
        </td>
      {{else if this.isExpired}}
        <td class="px-2 border-b align-middle" colspan={{this.deniedColspan}} data-test-expired>
          <div style="max-width: 500px;">
            {{#if this.cannotReviewInvite}}
              Invitation expired. An administrator did not approve this invite before the scheduled visit.
            {{else}}
              Invitation expired. Review ahead of visit to prevent this from happening.
            {{/if}}
          </div>
        </td>
      {{else}}
        {{#each @fields as |field|}}
          {{#if (and field.show (notEq field.name "Name"))}}
            {{#if (eq field.name "Purpose Of Visit")}}
              <div class="table-cell border-b align-top p-2 text-carbon-50">
                <div class="h-12 flex items-center">
                  <LineClamp @text={{@invite.flowName}} @lines={{2}} @lineHeight={{1.25}} title={{@invite.flowName}} />
                </div>
              </div>
            {{else if (eq field.name "Host")}}
              <div class="table-cell border-b align-top p-2 pt-4 text-carbon-50 w-56">
                <LineClamp @text={{@invite.host}} @lines={{2}} @lineHeight={{1.25}} title={{@invite.host}} />
              </div>
            {{else if (eq field.name "Private Notes")}}
              <div class="table-cell border-b align-top p-2 pt-4 text-carbon-50 max-w-[18rem]">
                {{#if @invite.privateNotes}}
                  <LineClamp
                    class="m-0 italic break-words"
                    @text={{@invite.privateNotes}}
                    @lines={{2}}
                    @lineHeight={{1.25}}
                    data-test-private-notes
                  />
                {{/if}}
              </div>
            {{else if (eq field.name "Been Here Before?")}}
              <div
                class="table-cell border-b align-top pl-2 pt-6 text-center text-carbon-50"
                data-test-returning-visitors-column
              >
                {{#if @invite.beenHereBefore}}
                  <img
                    alt="{{@invite.fullName}} is a returning visitor"
                    src="/assets/images/checkmark-small-gray.svg"
                    data-test-invite-returning-visitor
                  />
                {{/if}}
              </div>
            {{else if (eq field.name "Registration complete")}}
              <div class="table-cell border-b align-top pl-2 pt-6 text-carbon-50">
                {{#if @invite.preregistrationComplete}}
                  <img
                    data-test-registration-status="complete"
                    src="/assets/images/checkmark-small-gray.svg"
                    alt="registration completed"
                    class="mr-2"
                    style="width:11px; height:10px;"
                  />
                {{else}}
                  <img
                    data-test-registration-status="not complete"
                    src="/assets/images/cross-small-gray.svg"
                    alt="registration not completed"
                    class="mr-2"
                    style="width:11px; height:10px;"
                  />
                {{/if}}
              </div>
            {{else if (eq field.name "Group Name")}}
              <div data-test-group-name class="table-cell border-b align-top p-2 text-carbon-50">
                <div class="h-12 flex items-center">
                  {{#if (and @vrSubscription.canAccessGroupInviteRecords @invite.groupInviteId)}}
                    <LinkTo
                      @route="visitors.invites.groups.show"
                      @model={{@invite.groupInviteId}}
                      data-test-group-invite-link
                    >
                      <LineClamp
                        @useNative={{true}}
                        @text={{@invite.groupName}}
                        @lines={{2}}
                        @lineHeight={{1.25}}
                        title={{@invite.groupName}}
                      />
                    </LinkTo>
                  {{else}}
                    <LineClamp
                      @useNative={{true}}
                      @text={{@invite.groupName}}
                      @lines={{2}}
                      @lineHeight={{1.25}}
                      title={{@invite.groupName}}
                    />
                  {{/if}}
                </div>
              </div>
            {{else if (eq field.name "Employee Registration")}}
              <div class="table-cell border-b align-top pl-2 pt-6 text-carbon-50">
                {{#if @invite.isFromEmployeeScreening}}
                  {{#if @invite.approvalWasDenied}}
                    <img
                      data-test-emp-reg-status="denied"
                      src="/assets/images/cross-small-gray.svg"
                      alt=""
                      role="none"
                      class="mr-2"
                      style="width:11px; height:10px;"
                    />
                    Denied
                  {{else if @invite.approved}}
                    <img
                      data-test-emp-reg-status="approved"
                      src="/assets/images/circle-check.svg"
                      alt=""
                      role="none"
                      class="mr-2 float-left"
                      style="width:16px; height:16px;"
                    />
                    Approved
                  {{else}}
                    Scheduled
                  {{/if}}
                {{else}}
                  N/A
                {{/if}}
              </div>
            {{else if (eq field.name "Entry status")}}
              <div class="table-cell border-b align-top p-2 text-carbon-50" data-test-entry-status-cell>
                <div class="h-12 flex justify-center flex-col">
                  {{#if @invite.approvalWasDenied}}
                    <div class="flex items-center">
                      <img
                        data-test-emp-reg-status="denied"
                        src="/assets/images/cross-small-gray.svg"
                        alt=""
                        role="none"
                        class="mr-2"
                        style="width:11px; height:10px;"
                      />
                      Entry denied
                    </div>
                  {{else if @invite.approved}}
                    <div class="flex items-center">
                      <img
                        data-test-emp-reg-status="approved"
                        src="/assets/images/checkmark-small-gray.svg"
                        alt=""
                        role="none"
                        class="mr-2"
                        style="width:11px; height:10px;"
                      />
                      Approved
                    </div>
                  {{else if @invite.pending}}
                    Pending
                  {{else if @invite.needsPropertyApprovalReview}}
                    <div class="flex items-center">
                      <img
                        src="/assets/images/watch-later.svg"
                        alt=""
                        role="none"
                        class="mr-2"
                        style="width:14px; height:14px;"
                      />
                      Pending
                    </div>
                    <p class="text-xs mb-0 whitespace-nowrap">Property block list triggered.</p>
                    <p class="text-xs mb-0 whitespace-nowrap">You may contact your property team.</p>
                  {{else if @invite.needsApprovalReview}}
                    Review
                  {{/if}}
                </div>
              </div>
            {{else if (eq field.name "NDA")}}
              <PluginActivityColumnNda
                data-test-id="nda-column"
                @platformJobs={{awaitHelper this.entryForInvite.ndaPlatformJobs}}
                @agreementsStatus={{or this.entryForInvite.agreementsStatus @invite.agreementsStatus}}
              />
            {{else if (eq field.name "Assessments")}}
              <QuestionnairesStatusColumn
                data-test-id="questionnaires-column"
                @questionnaires={{mapBy "questionnaire" this.flowQuestionnaires}}
                @questionnaireResponseSummary={{@invite.questionnaireResponseSummary}}
              />
            {{else if (eq field.name "Blacklist")}}
              <BlocklistColumnComponent @content={{@invite}} />
            {{else if (eq field.name "Invited By")}}
              <div data-test-invited-by-cell class="table-cell border-b align-top p-2 text-carbon-50 w-56">
                <div class="h-12 flex items-center">
                  <LineClamp
                    @text={{this.creatorForInvite.fullName}}
                    @lines={{2}}
                    @lineHeight={{1.25}}
                    title={{this.creatorForInvite.fullName}}
                  />
                </div>
              </div>
            {{else if (eq field.name "Due At")}}
              <div data-test-due-at-cell class="table-cell border-b pl-2 align-middle text-carbon-50">
                {{#if @showFullDate}}
                  {{! TODO move date and datetime formats to util? }}
                  {{dateFormat
                    @invite.expectedArrivalTime
                    (if @invite.employeeScreeningFlow "MMM d, yyyy" "MMM d, yyyy - h:mm aaa")
                    timezone=@location.timezone
                  }}
                {{else}}
                  {{#unless @invite.employeeScreeningFlow}}
                    {{dateFormat @invite.expectedArrivalTime "h:mm aaa" timezone=@location.timezone}}
                  {{/unless}}
                {{/if}}
              </div>
            {{else if (eq field.name "Connect Notification")}}
              <div
                data-test-connect-notification-invite-id={{@invite.id}}
                class="table-cell border-b align-top pl-2 pt-6 text-carbon-50"
              >
                <ConnectNotificationActivity @model={{@invite}} />
              </div>
            {{else if (eq field.name "Shared Notes")}}
              <TruncatedColumn
                @value={{@invite.propertyNotes}}
                class="text-sm px-2"
                data-test-shared-notes
                data-test-shared-notes-invite-id={{@invite.id}}
              />
            {{else if (eq field.name "Arrived At")}}
              <ArrivedAtColumnComponent
                @timezone={{@location.timezone}}
                @value={{@invite.checkedInAt}}
                @showFullDate={{@showFullDate}}
                data-test-arrived-at
                data-test-arrived-at-invite-id={{@invite.id}}
              />
            {{else if (eq field.name "Signed In")}}
              <div data-test-invite-sign-in-time class="table-cell border-b align-middle pl-2 text-carbon-50">
                {{#if this.entryForInvite.signInTime}}
                  {{#if @showFullDate}}
                    {{dateFormat this.entryForInvite.signInTime "MMM d, yyyy - h:mm aaa" timezone=@location.timezone}}
                  {{else}}
                    {{dateFormat this.entryForInvite.signInTime "h:mm aaa" timezone=@location.timezone}}
                  {{/if}}
                {{/if}}
              </div>
            {{else if (eq field.name "Desk")}}
              <div class="table-cell border-b align-top pb-4 pl-2" data-test-reservations-cell>
                {{#each (sortBy "startTime" (rejectBy "canceledAt" @invite.reservations.content)) as |reservation|}}
                  <div class="{{if (can 'use partial day booking desk') 'pt-4' 'pt-6'}}" data-test-reservation>
                    <div class="text-carbon-70">{{reservation.desk.content.name}}
                      &#8226;
                      {{reservation.desk.content.floor.content.name}}</div>
                    {{#if (can "use partial day booking desk")}}
                      <div class="text-carbon-50">{{call (fn this.displayReservationTime reservation)}}</div>
                    {{/if}}
                  </div>
                {{/each}}
              </div>
            {{else if (eq field.name "Locations")}}
              <div class="table-cell border-b p-2 align-middle text-carbon-50">
                <LineClamp
                  @text={{this.locationNames}}
                  @lines={{2}}
                  @lineHeight={{1.25}}
                  title={{this.locationNames}}
                />
              </div>
            {{else if (eq field.componentName "custom-column")}}
              {{#if @invite.entry.content}}
                <CustomColumn @name={{field.name}} @content={{@invite.entry.content}} />
              {{else}}
                <CustomColumn @name={{field.name}} @content={{@invite}} />
              {{/if}}
            {{/if}}
          {{/if}}
        {{/each}}
      {{/if}}
      {{#if this.showActions}}
        {{! template-lint-disable attribute-indentation }}
        <div
          class="table-cell border-b align-top px-4 pt-2 text-center entry-actions-column sticky right-0 z-10 before:transition-shadow bg-white before:content-['']
            {{if @isSelected '!bg-carbon-5'}}
            {{if this.isUserDataInvalid '!bg-papaya-5'}}
            {{if
              @renderStickyShadows
              'before:shadow-[-4px_0_4px_0px_rgba(0,0,0,0.1)] before:h-full before:w-[4px] before:absolute before:left-0 before:top-0'
            }}
            "
          data-test-actions
        >
          <DropdownMenu
            @disabled={{this.hasNoOptions}}
            @left="-110"
            @top="-15"
            @onOpen={{fn this.trackOpenActionMenu @invite.id}}
          >
            <:trigger>
              <div
                class="invite-action-menu-trigger"
                data-test-options-handle
                data-test-invite-action-menu-trigger
                aria-label="invite options"
              >
                <svg
                  class="invite-action-menu-icon box-content"
                  width="18"
                  height="4"
                  viewBox="0 0 18 4"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.8 3.604a1.8 1.8 0 0 0 1.8-1.802 1.8 1.8 0 1 0-1.8 1.802zm7.2 0a1.8 1.8 0 0 0 1.8-1.802A1.8 1.8 0 1 0 9 3.604zm7.2 0A1.8 1.8 0 0 0 18 1.802a1.8 1.8 0 1 0-1.8 1.802z"
                    fill="#898B91"
                    fill-rule="evenodd"
                  />
                </svg>
              </div>
            </:trigger>
            <:content as |itemClass|>
              <div data-test-item-actions-menu class="entry-action-menu rounded-lg">
                {{#if this.canPrint}}
                  {{#if (gt @printers.length 1)}}
                    <UiButton
                      class="{{itemClass}} entries-sidebar-reprintBadge"
                      {{on "click" (set this "displayPrinterSelectorModal" true)}}
                      data-test-preprint-badge
                    >
                      Preprint badge
                    </UiButton>
                  {{else}}
                    <UiButton
                      class="{{itemClass}} entries-sidebar-reprintBadge"
                      data-test-preprint-badge
                      {{on "click" (fn this.preprintBadge @invite null)}}
                    >
                      Preprint badge
                    </UiButton>
                  {{/if}}
                {{/if}}
                {{#if this.canInviteAgain}}
                  <UiButton
                    class="{{itemClass}} entries-sidebar-inactive"
                    data-test-invite-again
                    {{on "click" (fn this.inviteAgain @invite)}}
                  >
                    Reinvite visitor
                  </UiButton>
                {{/if}}
                {{#if this.canInviteGroupAgain}}
                  <UiButton
                    class="{{itemClass}} entries-sidebar-inactive"
                    data-test-invite-group-again
                    {{on "click" (fn this.inviteGroupAgain @invite)}}
                  >
                    Reinvite group
                  </UiButton>
                {{/if}}
                {{#if this.canSignIn}}
                  <UiButton
                    class="{{itemClass}} entries-sidebar-signinVisitor {{if this.isUserDataInvalid 'disabled'}}"
                    disabled={{this.isUserDataInvalid}}
                    data-test-signin-invite
                    {{on "click" this.signIn}}
                  >
                    Sign in
                  </UiButton>
                {{/if}}
                {{#if (can "edit invite" @invite)}}
                  <UiButton class="{{itemClass}} entries-sidebar-editVisitor" data-test-edit-invite>
                    <LinkTo
                      @route={{if this.visitorIsSignedIn "visitors.entry" "visitors.invites.show"}}
                      @model={{if this.visitorIsSignedIn this.entryId @invite.id}}
                      @query={{this.query}}
                      data-test-invite-link-to-edit={{@invite.id}}
                    >
                      Edit invite
                    </LinkTo>
                  </UiButton>
                {{/if}}

                {{#if (and @invite.id (can "create blocklist in security"))}}
                  <UiButton
                    data-test-add-to-blocklist
                    class="{{itemClass}} entries-sidebar-addToBlocklist"
                    {{on "click" this.createBlocklistFilter}}
                  >
                    Add to Blocklist
                  </UiButton>
                {{/if}}

                {{#if this.canDelete}}
                  <UiButton
                    data-test-delete-invite
                    class="{{itemClass}} entries-sidebar-deleteVisitor"
                    {{on "click" (fn @deleteInvites @invite)}}
                  >
                    Delete invite
                  </UiButton>
                {{/if}}
              </div>
            </:content>
          </DropdownMenu>
        </div>
      {{/if}}

      {{#if this.showSignInModal}}
        <InviteSignInModal
          @invites={{array @invite}}
          @printers={{@printers}}
          @close={{this.closeSignIn}}
          @onSuccess={{this.onSuccessSignIn}}
        />
      {{/if}}

      {{#if this.displayPrinterSelectorModal}}
        <ReprintBadgeModal
          @model={{@invite}}
          @printers={{@printers}}
          @displayModal={{set this "displayPrinterSelectorModal"}}
          @reprintBadge={{this.preprintBadge}}
        />
      {{/if}}
    </div>

    {{#if this.blocklistFilterInModal}}
      <BlocklistFilterModal
        @blocklistFilter={{this.blocklistFilterInModal}}
        @close={{set this "blocklistFilterInModal" null}}
        @isSaving={{this.saveBlocklistFilterTask.isRunning}}
        @maxPhotoSizeKb={{10000}}
        @save={{fn this.saveBlocklistFilterTask.perform this.blocklistFilterInModal}}
      />
    {{/if}}
  </template>
}
